import { Controller } from "@hotwired/stimulus";

// Rows are broadcast to all viewers, so viewer-specific restrictions apply here:
// outside the admin view the head row is read-only and Schreibgeschützt is only toggled by a sub-head on their own row.
export default class extends Controller {
  static targets = ["headField", "headAction", "readOnlyToggle", "level", "writeProtection", "rateField"];
  static values = { rankId: Number };

  headFieldTargetConnected(element) {
    if (!this.isLockedHeadRow) return;

    element.classList.add("opacity-60");
    element.querySelectorAll("input, select, button").forEach((control) => {
      control.disabled = true;
    });
    element.querySelectorAll("select").forEach((select) => {
      // Re-attach so slim-select picks up the disabled state.
      this.application.getControllerForElementAndIdentifier(select, "slim-select")?.attach();
    });
  }

  headActionTargetConnected(element) {
    if (this.isLockedHeadRow) element.remove();
  }

  readOnlyToggleTargetConnected(element) {
    if (this.isAdminView) return;
    if (this.isLockedHeadRow && element.dataset.ownToggleable === "true") return;

    // Superiors see the protection but can't lift it.
    const checkbox = element.querySelector('input[type="checkbox"]');
    if (!checkbox?.checked) {
      element.replaceChildren();
      return;
    }

    element.querySelectorAll("input").forEach((input) => {
      input.disabled = true;
    });
    element.querySelectorAll(".cursor-pointer").forEach((control) => {
      control.classList.replace("cursor-pointer", "cursor-not-allowed");
    });
  }

  // Superiors can't change anything below a Schreibgeschützt box, nor the box itself apart from its rates.
  // Re-applied when a streamed marker reflects a changed protection.
  writeProtectionTargetConnected(marker) {
    if (!this.table || this.isAdminView || this.isHeadRow) return;

    // The table's head is the viewer's own box; a protection there or above doesn't apply to them.
    const rootLevel = Number(this.table.dataset.rootLevel);
    const belowProtected = JSON.parse(marker.dataset.readOnlyAncestorLevels).some((level) => level > rootLevel);
    const protectedBox = marker.dataset.readOnly === "true";

    this.element.querySelectorAll("input, select, button").forEach((control) => {
      if (this.readOnlyToggleTargets.some((toggle) => toggle.contains(control))) return;

      const isRate = this.rateFieldTargets.some((field) => field.contains(control));
      this.setWriteProtected(control, belowProtected || (protectedBox && !isRate));
    });
    this.element.querySelectorAll("select").forEach((select) => {
      this.application.getControllerForElementAndIdentifier(select, "slim-select")?.attach();
    });
  }

  // Only lifts locks it set itself, so controls disabled by the row's own rules stay disabled.
  setWriteProtected(control, locked) {
    if (locked && !control.disabled) {
      control.disabled = true;
      control.dataset.writeProtected = "";
    } else if (!locked && "writeProtected" in control.dataset) {
      control.disabled = false;
      delete control.dataset.writeProtected;
    } else {
      return;
    }

    if (control.type === "submit" || control.type === "checkbox") {
      control.classList.toggle("opacity-50", locked);
      control.classList.toggle("cursor-not-allowed", locked);
      control.classList.toggle("cursor-pointer", !locked);
    }
  }

  // Levels are stored absolute; show them relative to the table's head (level 1). The head
  // row, or a streamed level carrying the head's new level, keeps it current.
  levelTargetConnected(element) {
    const table = this.table;
    if (!table) return;

    if (this.isHeadRow) {
      table.dataset.rootLevel = element.dataset.level;
    } else if (element.dataset.rootLevel !== undefined) {
      table.dataset.rootLevel = element.dataset.rootLevel;
    }

    element.textContent = Number(element.dataset.level) - Number(table.dataset.rootLevel ?? 0) + 1;
  }

  get table() {
    return this.element.closest("#agent-ranks-table-body");
  }

  get isAdminView() {
    return this.table?.dataset.adminView === "true";
  }

  get isHeadRow() {
    return this.table?.dataset.rootRankId === String(this.rankIdValue);
  }

  get isLockedHeadRow() {
    return !this.isAdminView && this.isHeadRow;
  }
}
